import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { brl, sortProducts } from "@/api";
import { readableTextColor } from "@/utils/contrast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShoppingCart, Plus, Minus, ChevronRight, X, Search, MessageCircle, Instagram, MapPin, Phone, Info, Sun, Moon, Star, LayoutGrid, LayoutList } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function whatsappUrl(phone) {
  const clean = (phone || "").replace(/\D/g, "");
  if (!clean) return null;
  return `https://wa.me/${clean.length >= 11 ? "55" + clean : clean}`;
}

function instagramUrl(handle) {
  const h = (handle || "").replace(/^@/, "").trim();
  return h ? `https://instagram.com/${h}` : null;
}

// Garante contraste do texto branding contra o fundo do tema escolhido
function readableBrand(hex, isDark) {
  if (!hex || typeof hex !== "string") return isDark ? "#FAFAFA" : "#0A0A0A";
  const m = hex.replace("#", "");
  if (m.length !== 6) return isDark ? "#FAFAFA" : "#0A0A0A";
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  if (isDark && lum < 0.45) return "#FAFAFA";
  if (!isDark && lum > 0.85) return "#0A0A0A";
  return hex;
}

function PhotoGallery({ photos, primary }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const onScroll = () => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    setActive(Math.round(ref.current.scrollLeft / w));
  };
  if (!photos || photos.length === 0) {
    return <div className="aspect-square bg-neutral-100 flex items-center justify-center text-neutral-300 text-xs">sem foto</div>;
  }
  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={onScroll}
        className="aspect-square overflow-x-auto flex snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none" }}
        data-testid="product-photo-gallery"
      >
        {photos.map((src, i) => (
          <img key={i} src={src} alt="" className="w-full h-full object-cover flex-shrink-0 snap-center" style={{ width: "100%" }}/>
        ))}
      </div>
      {photos.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
          {photos.map((_, i) => (
            <span key={i}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                background: active === i ? primary : "rgba(255,255,255,0.7)",
                width: active === i ? 16 : 6,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StoreProfileDialog({ open, onClose, store, cfg, primary, isDark }) {
  const wa = whatsappUrl(store.phone);
  const ig = instagramUrl(store.instagram);
  const brandColor = readableBrand(primary, isDark);
  return (
    <Dialog open={open} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-0">
        <button onClick={onClose} className="absolute right-3 top-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm">
          <X size={16}/>
        </button>
        <div className="p-8 text-center border-b border-[#E5E5E5]">
          {cfg.show_logo && store.logo
            ? <img src={store.logo} alt="" className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border border-[#E5E5E5]"/>
            : <div className="w-24 h-24 rounded-full bg-neutral-100 mx-auto mb-4"/>}
          {cfg.show_name && <h2 className="font-display text-2xl tracking-tight" style={{ color: brandColor }}>{store.name || "Loja"}</h2>}
          {cfg.show_phone && store.phone && <div className="flex items-center justify-center gap-2 text-sm text-neutral-600 mt-2"><Phone size={14}/> {store.phone}</div>}
          {cfg.show_address && store.address && <div className="flex items-start justify-center gap-2 text-sm text-neutral-600 mt-2 max-w-xs mx-auto"><MapPin size={14} className="mt-0.5 flex-shrink-0"/><span>{store.address}</span></div>}
        </div>
        <div className="p-6 space-y-3">
          {cfg.show_whatsapp && wa && (
            <a href={wa} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-3 rounded-md bg-green-500 hover:bg-green-600 text-white font-medium transition" data-testid="profile-whatsapp">
              <MessageCircle size={18}/> Falar no WhatsApp
            </a>
          )}
          {cfg.show_instagram && ig && (
            <a href={ig} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-3 rounded-md text-white font-medium transition" style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }} data-testid="profile-instagram">
              <Instagram size={18}/> @{(store.instagram || "").replace(/^@/, "")}
            </a>
          )}
          {cfg.show_about && store.about && (
            <div className="border-t border-[#E5E5E5] pt-4">
              <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-neutral-500"><Info size={12}/> Sobre</div>
              <p className="text-sm text-neutral-600 whitespace-pre-line">{store.about}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductDetail({ product, primary, accent, onClose, onAdd, isDark, subtitlesMap = {} }) {
  const hasVariants = (product.variants || []).length > 0;
  const [selVar, setSelVar] = useState(null);
  const [qty, setQty] = useState(1);
  // Para produtos COM variantes: { [variantName]: quantity } — múltiplas seleções de uma vez
  const [variantQty, setVariantQty] = useState({});
  const variant = selVar ? product.variants.find(v => v.name === selVar) : null;
  const price = variant ? variant.price : (product.promo_price || product.price);
  const label = product.variants_label || "Variação";
  const allow = !!product.allow_oversell;
  const brandColor = readableBrand(primary, isDark);
  const available = hasVariants
    ? (variant ? Number(variant.quantity || 0) : 0)
    : Number(product.stock || 0);
  const outOfStock = !allow && available <= 0 && (hasVariants ? !!variant : true);
  const canIncrease = allow || qty < available;

  // Total das variantes selecionadas (modo multi)
  const variantsTotal = useMemo(() => (product.variants || []).reduce((s, v) => {
    const q = Number(variantQty[v.name] || 0);
    if (q <= 0) return s;
    const p = Number(v.price ?? product.promo_price ?? product.price ?? 0);
    return s + p * q;
  }, 0), [variantQty, product]);
  const variantsCount = useMemo(() => Object.values(variantQty).reduce((s, q) => s + Number(q || 0), 0), [variantQty]);

  const incVariant = (v) => {
    const cur = Number(variantQty[v.name] || 0);
    const max = allow ? Infinity : Number(v.quantity || 0);
    if (cur >= max) return;
    setVariantQty(prev => ({ ...prev, [v.name]: cur + 1 }));
  };
  const decVariant = (v) => {
    const cur = Number(variantQty[v.name] || 0);
    if (cur <= 0) return;
    setVariantQty(prev => ({ ...prev, [v.name]: cur - 1 }));
  };
  const setVariantQtyExact = (v, val) => {
    const max = allow ? Infinity : Number(v.quantity || 0);
    const n = Math.max(0, Math.min(max, Number(val) || 0));
    setVariantQty(prev => ({ ...prev, [v.name]: n }));
  };

  // Gallery: SEMPRE fotos do produto (não troca conforme variante; usuário escolhe múltiplas)
  const galleryPhotos = useMemo(() => {
    const arr = [];
    (product.photos || []).forEach(p => { if (p && !arr.includes(p)) arr.push(p); });
    return arr;
  }, [product.photos]);

  const confirmSimple = () => {
    if (!allow && qty > available) {
      toast.error(`Apenas ${available} disponível.`);
      return;
    }
    onAdd(product, null, qty);
    toast.success(`${qty}× ${product.name} adicionado`);
    onClose();
  };

  const confirmVariants = () => {
    const selections = (product.variants || []).filter(v => Number(variantQty[v.name] || 0) > 0);
    if (selections.length === 0) { toast.error("Selecione ao menos uma opção"); return; }
    selections.forEach(v => onAdd(product, v.name, Number(variantQty[v.name])));
    toast.success(selections.length === 1 ? "Item adicionado" : `${selections.length} itens adicionados`);
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-0">
        <button onClick={onClose} className="absolute right-3 top-3 z-20 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm" data-testid="product-detail-close">
          <X size={16}/>
        </button>
        <PhotoGallery photos={galleryPhotos} primary={primary}/>
        <div className="p-6 space-y-4">
          <div>
            <h2 className="font-display text-xl tracking-tight" style={{ color: brandColor }}>{product.name}</h2>
            {(product.catalog_subtitle_ids || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="product-detail-subtitles">
                {product.catalog_subtitle_ids.map(sid => {
                  const s = subtitlesMap[sid];
                  if (!s) return null;
                  const bg = s.bg_color || "#FEF3C7";
                  return (
                    <span key={sid}
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                      style={{ background: bg, color: readableTextColor(bg) }}
                    >
                      {s.text}
                    </span>
                  );
                })}
              </div>
            )}
            <div className="mt-1">
              {!variant && product.promo_price ? (
                <>
                  <span className="text-sm line-through text-neutral-400 mr-2">{brl(product.price)}</span>
                  <span className="font-display text-2xl" style={{ color: accent }}>{brl(product.promo_price)}</span>
                </>
              ) : (
                <span className="font-display text-2xl" style={{ color: brandColor }}>{brl(price)}</span>
              )}
            </div>
            {product.description && (
              <div className="mt-3 text-sm text-neutral-600 whitespace-pre-line border-t border-[#E5E5E5] pt-3" data-testid="product-description">
                {product.description}
              </div>
            )}
          </div>

          {hasVariants && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Selecionar {label}</Label>
              <div className="mt-2 divide-y divide-[#F0F0F0] border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="variant-grid">
                {product.variants.map(v => {
                  const stockNum = Number(v.quantity || 0);
                  const vOut = !allow && stockNum <= 0;
                  const productPrice = Number(product.promo_price ?? product.price ?? 0);
                  const variantPrice = Number(v.price ?? productPrice);
                  const hasPromo = v.price != null && productPrice > variantPrice && productPrice > 0;
                  const q = Number(variantQty[v.name] || 0);
                  const photo = v.photo || product.photos?.[0] || null;
                  return (
                    <div
                      key={v.name}
                      className={`flex items-stretch gap-3 p-3 ${vOut ? "opacity-50" : ""}`}
                      data-testid={`variant-option-${v.name}`}
                    >
                      <button
                        type="button"
                        onClick={() => !vOut && incVariant(v)}
                        disabled={vOut}
                        className="relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-neutral-100 border border-[#E5E5E5]"
                      >
                        {photo
                          ? <img src={photo} alt={v.name} className="w-full h-full object-cover"/>
                          : <div className="w-full h-full"/>}
                        {q > 0 && (
                          <div className="absolute top-0 left-0 bg-emerald-500 text-white text-[11px] font-bold w-6 h-6 flex items-center justify-center rounded-br-md">
                            {q}
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => !vOut && incVariant(v)}
                        disabled={vOut}
                        className="flex-1 text-left min-w-0 disabled:cursor-not-allowed"
                      >
                        <p className="text-sm font-medium truncate">{v.name}</p>
                        {vOut && <p className="text-[11px] text-red-600 mt-0.5">Esgotada</p>}
                      </button>
                      <div className="flex flex-col items-end justify-between">
                        <div className="text-right">
                          {hasPromo && <div className="text-[11px] text-neutral-400 line-through">{brl(productPrice)}</div>}
                          <div className="text-sm font-medium">{brl(variantPrice)}</div>
                        </div>
                        {q > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <button onClick={()=>decVariant(v)} className="h-6 w-6 rounded border border-[#E5E5E5] flex items-center justify-center hover:bg-neutral-50"><Minus size={11}/></button>
                            <Input value={q} onChange={(e)=>setVariantQtyExact(v, e.target.value)} className="h-6 w-10 text-center text-xs px-1"/>
                            <button onClick={()=>incVariant(v)} disabled={!allow && q >= stockNum} className="h-6 w-6 rounded border border-[#E5E5E5] flex items-center justify-center hover:bg-neutral-50 disabled:opacity-40"><Plus size={11}/></button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!hasVariants && (
            <div className="flex items-center justify-between border-t border-[#E5E5E5] pt-4">
              <Label>Quantidade</Label>
              <div className="flex items-center gap-2 border border-[#E5E5E5] rounded-md">
                <Button variant="ghost" size="sm" onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="qty-decrease"><Minus size={14}/></Button>
                <span className="w-8 text-center text-sm" data-testid="qty-value">{qty}</span>
                <Button variant="ghost" size="sm" onClick={() => canIncrease && setQty(q => q + 1)} disabled={!canIncrease} data-testid="qty-increase"><Plus size={14}/></Button>
              </div>
            </div>
          )}

          {hasVariants ? (
            <div className="flex items-center gap-3 border-t border-[#E5E5E5] pt-4">
              <div className="text-right">
                <p className="font-display text-xl tracking-tight" style={{ color: accent }}>{brl(variantsTotal)}</p>
                <p className="text-[11px] text-neutral-500">{variantsCount} {variantsCount === 1 ? "item" : "itens"}</p>
              </div>
              <Button
                onClick={confirmVariants}
                disabled={variantsCount === 0}
                style={{ background: variantsCount === 0 ? "#999" : primary }}
                className="flex-1 text-white py-6"
                data-testid="add-to-cart-confirm"
              >
                Adicionar ao carrinho
              </Button>
            </div>
          ) : (
            <Button
              onClick={confirmSimple}
              disabled={outOfStock}
              style={{ background: outOfStock ? "#999" : primary }}
              className="w-full text-white py-6"
              data-testid="add-to-cart-confirm"
            >
              {outOfStock ? "Esgotado" : `Adicionar — ${brl(price * qty)}`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PublicCatalog() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Inicializa lendo o cache do sessionStorage SINCRONAMENTE — assim o primeiro
  // render já tem cfg.dark_mode correto, evitando o "flash" de tema misturado.
  // Também detecta se temos cache válido para NÃO exibir loading no primeiro paint.
  const cachedInitial = (() => {
    try {
      const cached = sessionStorage.getItem(`vendiz_catalog_cache_${slug}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.tenant) return parsed;
      }
    } catch {}
    return null;
  })();
  const [data, setData] = useState(() => cachedInitial || { enabled: true, products: [], store: {}, config: {}, categories: [] });
  // loading = true até o fetch completar (falso se já temos cache pra evitar flash).
  const [loading, setLoading] = useState(!cachedInitial);
  const [cart, setCart] = useState({}); // {key: {product_id, variant_name, qty, snapshot}}
  const [openCheckout, setOpenCheckout] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [openProduct, setOpenProduct] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_address: "", note: "" });
  const [done, setDone] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const cacheKey = `vendiz_catalog_cache_${slug}`;
    axios.get(`${API}/public/${slug}/catalog`).then(r => {
      setData(r.data);
      if (!r.data.tenant) setNotFound(true);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(r.data)); } catch {}
      // Persiste o tema padrão da loja em localStorage para a próxima visita
      // ser instantânea (mesmo antes do fetch completar).
      try {
        const isDarkConfig = !!(r.data?.config?.dark_mode);
        localStorage.setItem(`vendiz_catalog_default_dark_${slug}`, isDarkConfig ? "1" : "0");
      } catch {}
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  // Deep link: ?product=<id> abre o modal do produto automaticamente.
  // Ao fechar, limpamos o query param e o catálogo volta para a home.
  useEffect(() => {
    const pid = searchParams.get("product");
    if (!pid) return;
    if (!data.products || data.products.length === 0) return;
    if (openProduct && openProduct.id === pid) return;
    const found = data.products.find(p => p.id === pid);
    if (found) {
      setOpenProduct(found);
      // Busca detalhes completos
      axios.get(`${API}/public/${slug}/product/${pid}`).then(r => {
        setOpenProduct(curr => curr && curr.id === pid ? r.data : curr);
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [data.products, searchParams, slug]);

  const cfg = data.config || {};
  const accent = cfg.accent_color || "#002FA7";
  const primary = cfg.primary_color || "#0A0A0A";

  // Tema do catálogo: padrão = cfg.dark_mode; visitante pode override (salvo em localStorage por slug)
  const themeKey = `vendiz_catalog_theme_${slug}`;
  const [visitorTheme, setVisitorTheme] = useState(() => {
    try { return localStorage.getItem(themeKey); } catch { return null; }
  });
  // Fallback: se ainda não temos data carregado nesta sessão, usamos o último
  // dark_mode conhecido da loja (persistido em localStorage na visita anterior).
  const hasConfig = Object.keys(cfg).length > 0;
  const cachedDefaultDark = (() => {
    if (hasConfig) return null;
    try { return localStorage.getItem(`vendiz_catalog_default_dark_${slug}`); } catch { return null; }
  })();
  const isDark = visitorTheme
    ? visitorTheme === "dark"
    : hasConfig
      ? !!cfg.dark_mode
      : cachedDefaultDark === "1";

  // Aplica a classe .dark no <html> DURANTE o render (antes de qualquer paint).
  // useLayoutEffect roda APÓS o primeiro render do React, o que ainda causa
  // micro-flash quando o usuário chega de uma rota com tema diferente. Esta
  // chamada é idempotente (só troca se estiver diferente) e roda a cada render
  // — como o valor de isDark é estável, não há custo perceptível.
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    const hasClass = root.classList.contains("dark");
    if (isDark && !hasClass) root.classList.add("dark");
    else if (!isDark && hasClass) root.classList.remove("dark");
  }

  // Safety net: garante a classe no mount/unmount/slug change.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [isDark]);

  // Restore admin theme on unmount (in case user navigates back to /admin in same tab)
  useEffect(() => {
    return () => {
      try {
        const adminTheme = localStorage.getItem("vendiz_theme");
        const root = document.documentElement;
        if (adminTheme === "dark") root.classList.add("dark");
        else root.classList.remove("dark");
      } catch {}
    };
  }, []);

  const toggleTheme = () => {
    const next = isDark ? "light" : "dark";
    setVisitorTheme(next);
    try { localStorage.setItem(themeKey, next); } catch {}
  };

  // ── Modo de visualização (lista ↔ grade) ─────────────────────────────────
  // Default: vem do admin (cfg.layout). Visitante pode overridar — salvo em
  // localStorage por slug. Cache: último layout default conhecido (igual ao
  // dark_mode, pra evitar flash quando ainda não carregou).
  const viewKey = `vendiz_catalog_view_${slug}`;
  const [visitorView, setVisitorView] = useState(() => {
    try { return localStorage.getItem(viewKey); } catch { return null; }
  });
  // Persiste o layout default vindo do admin (pra próxima visita carregar instantâneo)
  useEffect(() => {
    if (hasConfig && cfg.layout) {
      try { localStorage.setItem(`vendiz_catalog_default_view_${slug}`, cfg.layout); } catch {}
    }
  }, [hasConfig, cfg.layout, slug]);
  const cachedDefaultView = (() => {
    if (hasConfig) return null;
    try { return localStorage.getItem(`vendiz_catalog_default_view_${slug}`); } catch { return null; }
  })();
  const viewMode = visitorView
    ? (visitorView === "grid" ? "grid" : "list")
    : (hasConfig
        ? (cfg.layout === "grid" ? "grid" : "list")
        : (cachedDefaultView === "grid" ? "grid" : "list"));
  const toggleView = () => {
    const next = viewMode === "grid" ? "list" : "grid";
    setVisitorView(next);
    try { localStorage.setItem(viewKey, next); } catch {}
  };

  const surface = isDark ? "#18181B" : "#FFFFFF";
  const border = isDark ? "#2A2A2E" : "#E5E5E5";
  const textMain = isDark ? "#FAFAFA" : "#0A0A0A";
  const textMuted = isDark ? "#A1A1AA" : "#71717A";

  const items = useMemo(() => {
    return Object.entries(cart).filter(([, v]) => v.qty > 0).map(([key, v]) => {
      const p = data.products.find(x => x.id === v.product_id);
      if (!p) return null;
      const variant = v.variant_name ? (p.variants || []).find(x => x.name === v.variant_name) : null;
      const price = variant ? variant.price : (p.promo_price || p.price);
      const photo = variant?.photo || p.photos?.[0];
      return { key, product: p, variant, name: p.name + (v.variant_name ? ` (${v.variant_name})` : ""), price, photo, qty: v.qty };
    }).filter(Boolean);
  }, [cart, data.products]);

  const total = items.reduce((s, x) => s + x.price * x.qty, 0);
  const cartCount = items.reduce((s, x) => s + x.qty, 0);

  const catsMap = useMemo(() => Object.fromEntries((data.categories || []).map(c => [c.id, c])), [data.categories]);
  const subtitlesMap = useMemo(() => Object.fromEntries((data.subtitles || []).map(s => [s.id, s])), [data.subtitles]);
  const filtered = useMemo(() => {
    let list = data.products;
    if (search.trim()) {
      const t = search.trim().toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(t));
    }
    if (activeCategory !== "all") {
      list = list.filter(p => p.category_id === activeCategory);
    }
    return sortProducts(list, catsMap);
  }, [data.products, search, activeCategory, catsMap]);

  const categories = useMemo(() => {
    const used = new Set(data.products.map(p => p.category_id).filter(Boolean));
    return (data.categories || []).filter(c => used.has(c.id));
  }, [data.products, data.categories]);

  const addToCart = (product, variantName, qty) => {
    const k = `${product.id}::${variantName || ""}`;
    setCart(c => ({ ...c, [k]: { product_id: product.id, variant_name: variantName || null, qty: (c[k]?.qty || 0) + qty } }));
    toast.success("Adicionado ao carrinho");
  };

  const openProductDetail = async (p) => {
    // Se o produto da listagem já tem photos da variante (cenário raro pós-mudança
    // no backend), abre direto. Caso contrário, busca o detalhe FULL ANTES de abrir
    // — listagem usa endpoint light que descarta `photo` das variantes para reduzir
    // payload, e abrir o modal sem aguardar provocava "às vezes funciona, às vezes
    // não" (race condition: usuário interagia antes do fetch completar).
    const alreadyFull = (p.variants || []).some(v => v.photo) || (p.photos || []).length > 1;
    if (alreadyFull) {
      setOpenProduct(p);
      return;
    }
    setOpenProduct({ ...p, _loading: true });
    try {
      const r = await axios.get(`${API}/public/${slug}/product/${p.id}`);
      setOpenProduct(curr => curr && curr.id === p.id ? r.data : curr);
    } catch {
      setOpenProduct(curr => curr && curr.id === p.id ? { ...p, _loading: false } : curr);
    }
  };

  const updQty = (key, delta) => setCart(c => {
    const cur = c[key];
    if (!cur) return c;
    const newQty = Math.max(0, cur.qty + delta);
    if (newQty === 0) {
      const { [key]: _, ...rest } = c;
      return rest;
    }
    return { ...c, [key]: { ...cur, qty: newQty } };
  });

  const removeItem = (key) => setCart(c => {
    const { [key]: _, ...rest } = c;
    return rest;
  });

  // Helper: fecha o modal do produto, limpa o ?product= da URL e leva ao topo da home
  const closeProductModal = () => {
    setOpenProduct(null);
    if (searchParams.get("product")) {
      const next = new URLSearchParams(searchParams);
      next.delete("product");
      setSearchParams(next, { replace: true });
    }
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };

  // Helper: depois de enviar pedido, "volta" para a home do catálogo
  const resetCatalogToHome = () => {
    setOpenCheckout(false);
    setOpenCart(false);
    setOpenProduct(null);
    setSearch("");
    setActiveCategory("all");
    if (searchParams.get("product")) {
      const next = new URLSearchParams(searchParams);
      next.delete("product");
      setSearchParams(next, { replace: true });
    }
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };

  const submit = async () => {
    if (!form.customer_name || !form.customer_phone) return toast.error("Nome e telefone obrigatórios");
    if (items.length === 0) return toast.error("Carrinho vazio");
    try {
      const r = await axios.post(`${API}/public/${slug}/orders`, {
        ...form,
        items: items.map(x => ({ product_id: x.product.id, variant_name: x.variant?.name || null, quantity: x.qty })),
      });
      setDone(r.data);
      setCart({});
      setForm({ customer_name: "", customer_phone: "", customer_address: "", note: "" });
      resetCatalogToHome();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar pedido");
    }
  };

  const submitWhatsApp = async () => {
    if (!form.customer_name || !form.customer_phone) return toast.error("Nome e telefone obrigatórios");
    if (items.length === 0) return toast.error("Carrinho vazio");
    const storeWa = whatsappUrl(data.store?.phone);
    if (!storeWa) return toast.error("Loja sem WhatsApp cadastrado");

    // Detecta mobile: nesses dispositivos abrir nova aba causa "tela branca"
    // residual após o WhatsApp app abrir. Em mobile, redireciona na MESMA aba.
    // Em desktop, mantém nova aba (caso contrário o usuário perde o catálogo).
    const isMobile = window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;

    // Em desktop precisa abrir a janela ANTES do await (Safari/iOS bloqueiam popup
    // se vier após chamada assíncrona).
    const waWindow = isMobile ? null : window.open("", "_blank");

    let orderResp = null;
    try {
      const r = await axios.post(`${API}/public/${slug}/orders`, {
        ...form,
        items: items.map(x => ({ product_id: x.product.id, variant_name: x.variant?.name || null, quantity: x.qty })),
      });
      orderResp = r.data;
    } catch (e) {
      if (waWindow && !waWindow.closed) waWindow.close();
      toast.error(e.response?.data?.detail || "Erro ao registrar pedido");
      return;
    }
    const orderNum = String(orderResp.order_number || "").padStart(6, "0");
    const lines = [];
    lines.push("Olá! Tenho interesse neste pedido:");
    lines.push("");
    if (orderNum) lines.push(`Pedido: #${orderNum}`);
    lines.push(`Cliente: ${form.customer_name}`);
    lines.push(`Telefone: ${form.customer_phone}`);
    if (form.customer_address) lines.push(`Endereço: ${form.customer_address}`);
    lines.push("");
    lines.push("Itens:");
    items.forEach(it => {
      lines.push(`• ${it.name} × ${it.qty} — ${brl(it.price * it.qty)}`);
    });
    lines.push("");
    lines.push(`Total: ${brl(total)}`);
    if (form.note) {
      lines.push("");
      lines.push(`Obs.: ${form.note}`);
    }
    const text = encodeURIComponent(lines.join("\n"));
    const finalUrl = `${storeWa}?text=${text}`;

    // Limpa estado ANTES do redirect — assim ao voltar do WhatsApp o catálogo
    // já está na home, sem tela branca.
    setDone(orderResp);
    setCart({});
    setForm({ customer_name: "", customer_phone: "", customer_address: "", note: "" });
    resetCatalogToHome();

    if (isMobile) {
      // Mesma aba: o WhatsApp abre via deeplink no app; ao voltar, o catálogo
      // já está resetado para a home.
      window.location.href = finalUrl;
    } else if (waWindow && !waWindow.closed) {
      waWindow.location.href = finalUrl;
    } else {
      // popup bloqueado em desktop: abre na mesma aba como fallback
      window.location.href = finalUrl;
    }
  };

  const [openProfile, setOpenProfile] = useState(false);

  if (notFound) {
    return <div className="min-h-screen flex items-center justify-center bg-white p-8 text-center">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Loja não encontrada</h1>
        <p className="text-sm text-neutral-500 mt-2">O link `/catalogo/{slug}` não existe ou foi desativado.</p>
      </div>
    </div>;
  }

  if (!data.enabled) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-neutral-500">Catálogo temporariamente indisponível.</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-32" data-testid="public-catalog-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-[#E5E5E5]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <button onClick={()=>setOpenProfile(true)} className="flex items-center gap-3 min-w-0 hover:opacity-80 transition" data-testid="open-store-profile">
            {cfg.show_logo && data.store?.logo
              ? <img src={data.store.logo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0"/>
              : (cfg.show_logo ? <div className="w-10 h-10 rounded-full bg-neutral-100 flex-shrink-0"/> : null)}
            <div className="min-w-0 text-left">
              {cfg.show_name && <div className="font-display tracking-tight truncate" style={{ color: readableBrand(primary, isDark) }}>{data.store?.name || "Loja"}</div>}
              {cfg.show_phone && data.store?.phone && <div className="text-xs text-neutral-500 truncate">{data.store.phone}</div>}
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleView}
              className="w-11 h-11 rounded-full border flex items-center justify-center transition"
              style={{ background: surface, color: textMain, borderColor: border }}
              title={viewMode === "grid" ? "Mudar para Lista" : "Mudar para Grade"}
              aria-label="Alternar visualização"
              data-testid="catalog-view-toggle"
            >
              {viewMode === "grid" ? <LayoutList size={16}/> : <LayoutGrid size={16}/>}
            </button>
            <button
              onClick={toggleTheme}
              className="w-11 h-11 rounded-full border flex items-center justify-center transition"
              style={{ background: surface, color: textMain, borderColor: border }}
              title={isDark ? "Mudar para claro" : "Mudar para escuro"}
              aria-label="Alternar tema"
              data-testid="catalog-theme-toggle-public"
            >
              {isDark ? <Sun size={16}/> : <Moon size={16}/>}
            </button>
            <button
              onClick={()=>setOpenCart(true)}
              className="relative w-11 h-11 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center"
              style={{ background: primary }}
              data-testid="open-cart-button"
            >
              <ShoppingCart size={18}/>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-white text-[#0A0A0A] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border border-[#E5E5E5]">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
        {/* Search + Categories */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto">
          <div className="relative flex-shrink-0 w-44">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: textMuted }}/>
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar…" className="pl-9 h-10 rounded-full" style={{ background: isDark ? "#27272A" : "#F4F4F5", color: isDark ? "#FAFAFA" : "#0A0A0A", borderColor: border }} data-testid="catalog-search-input"/>
          </div>
          <button
            onClick={()=>setActiveCategory("all")}
            className={`px-5 h-10 rounded-full text-sm whitespace-nowrap transition border`}
            style={activeCategory === "all"
              ? { background: primary, color: "white", borderColor: primary }
              : { background: surface, color: textMain, borderColor: border }}
            data-testid="cat-all"
          >Todos</button>
          {categories.map(c => (
            <button
              key={c.id}
              onClick={()=>setActiveCategory(c.id)}
              className={`px-5 h-10 rounded-full text-sm whitespace-nowrap transition border`}
              style={activeCategory === c.id
                ? { background: primary, color: "white", borderColor: primary }
                : { background: surface, color: textMain, borderColor: border }}
            >
              {c.name}
            </button>
          ))}
        </div>
      </header>

      {/* Hero */}
      {(cfg.show_about && data.store?.about) || (cfg.show_address && data.store?.address) ? (
        <section className="max-w-3xl mx-auto px-4 pt-6 pb-2">
          {cfg.show_about && data.store?.about && <p className="text-sm" style={{ color: isDark ? "#D4D4D8" : "#525252" }}>{data.store.about}</p>}
          {cfg.show_address && data.store?.address && <p className="text-xs mt-1" style={{ color: textMuted }}>{data.store.address}</p>}
        </section>
      ) : null}

      {/* Product list/grid */}
      <section
        className={`${viewMode === "grid" ? "max-w-7xl" : "max-w-3xl"} mx-auto px-4 py-6 transition-all`}
        data-testid={`catalog-products-${viewMode}`}
      >
        {viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
            {filtered.map(p => {
              const hasVariants = (p.variants || []).length > 0;
              const photo = p.photos?.[0];
              const price = p.promo_price || p.price;
              const isOffer = !!p.promo_price;
              const allow = !!p.allow_oversell;
              const productEsgotado = !allow && (
                hasVariants
                  ? (p.variants || []).every(v => Number(v.quantity || 0) <= 0)
                  : Number(p.stock || 0) <= 0
              );
              return (
                <button
                  key={p.id}
                  onClick={()=>openProductDetail(p)}
                  className="group relative rounded-2xl overflow-hidden text-left transition duration-200 hover:shadow-xl hover:-translate-y-0.5 motion-reduce:transition-none"
                  style={{ background: surface, border: `1px solid ${border}` }}
                  data-testid={`catalog-product-${p.id}`}
                >
                  <div className="relative aspect-square w-full overflow-hidden" style={{ background: isDark ? "#27272A" : "#F4F4F5" }}>
                    {photo
                      ? <img src={photo} alt={p.name} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${productEsgotado ? "opacity-50" : ""}`}/>
                      : <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: isDark ? "#52525B" : "#D4D4D8" }}>sem foto</div>}
                    {isOffer && !productEsgotado && (
                      <span className="absolute top-2.5 left-2.5 text-white text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full shadow-sm" style={{ background: primary }}>OFERTA</span>
                    )}
                    {p.featured && !productEsgotado && (
                      <span className="absolute top-2.5 right-2.5 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                        <Star size={9} fill="currentColor"/> Destaque
                      </span>
                    )}
                    {productEsgotado && (
                      <span className="absolute top-2.5 left-2.5 bg-neutral-700 text-white text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full">ESGOTADO</span>
                    )}
                    {/* Botão carrinho minimalista (canto inferior) */}
                    <div
                      className="absolute bottom-2.5 right-2.5 w-9 h-9 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity sm:opacity-100"
                      style={{ background: productEsgotado ? "#9CA3AF" : primary, color: "white" }}
                      aria-hidden="true"
                    >
                      <Plus size={16}/>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="text-[13px] sm:text-sm leading-snug line-clamp-2 min-h-[2.5rem]" style={{ color: textMain }}>{p.name}</div>
                    {(p.catalog_subtitle_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`catalog-product-subtitles-${p.id}`}>
                        {p.catalog_subtitle_ids.map(sid => {
                          const s = subtitlesMap[sid];
                          if (!s) return null;
                          const bg = s.bg_color || "#FEF3C7";
                          return (
                            <span key={sid}
                              className="text-[9px] sm:text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                              style={{ background: bg, color: readableTextColor(bg) }}
                            >
                              {s.text}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                      {isOffer && <span className="text-[10px] line-through" style={{ color: textMuted }}>{brl(p.price)}</span>}
                      <span className="font-display text-base sm:text-lg leading-none" style={{ color: textMain }}>{brl(price)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => {
              const hasVariants = (p.variants || []).length > 0;
              const photo = p.photos?.[0];
              const price = p.promo_price || p.price;
              const isOffer = !!p.promo_price;
              const allow = !!p.allow_oversell;
              const productEsgotado = !allow && (
                hasVariants
                  ? (p.variants || []).every(v => Number(v.quantity || 0) <= 0)
                  : Number(p.stock || 0) <= 0
              );
              return (
                <button
                  key={p.id}
                  onClick={()=>openProductDetail(p)}
                  className="w-full rounded-xl border overflow-hidden flex items-stretch text-left transition shadow-sm"
                  style={{ background: surface, borderColor: border }}
                  data-testid={`catalog-product-${p.id}`}
                >
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0" style={{ background: isDark ? "#27272A" : "#F4F4F5" }}>
                    {photo
                      ? <img src={photo} alt={p.name} loading="lazy" decoding="async" className={`w-full h-full object-cover ${productEsgotado ? "opacity-50" : ""}`}/>
                      : <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: isDark ? "#52525B" : "#D4D4D8" }}>sem foto</div>}
                    {isOffer && !productEsgotado && (
                      <span className="absolute top-2 left-0 text-white text-[9px] font-bold tracking-widest px-2 py-0.5" style={{ background: primary }}>OFERTA</span>
                    )}
                    {p.featured && !productEsgotado && (
                      <span className="absolute top-2 right-2 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                        <Star size={9} fill="currentColor"/> Destaque
                      </span>
                    )}
                    {productEsgotado && (
                      <span className="absolute top-2 left-0 bg-neutral-700 text-white text-[9px] font-bold tracking-widest px-2 py-0.5">ESGOTADO</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col justify-between">
                    <div>
                      <div className="text-sm sm:text-base leading-snug line-clamp-2" style={{ color: textMain }}>{p.name}</div>
                      {(p.catalog_subtitle_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`catalog-product-subtitles-${p.id}`}>
                          {p.catalog_subtitle_ids.map(sid => {
                            const s = subtitlesMap[sid];
                            if (!s) return null;
                            const bg = s.bg_color || "#FEF3C7";
                            return (
                              <span key={sid}
                                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                                style={{ background: bg, color: readableTextColor(bg) }}
                              >
                                {s.text}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex items-end justify-between gap-2 mt-2">
                      <div>
                        {isOffer && <div className="text-[10px] line-through" style={{ color: textMuted }}>{brl(p.price)}</div>}
                        <div className="font-display text-lg sm:text-xl" style={{ color: textMain }}>{brl(price)}</div>
                        {hasVariants && (
                          <div className="text-[11px] mt-0.5" style={{ color: textMuted }}>{p.variants.length} {p.variants.length === 1 ? "opção" : "opções"}</div>
                        )}
                      </div>
                      <div
                        className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ background: productEsgotado ? "#9CA3AF" : primary, color: "white" }}
                      >
                        <ChevronRight size={18}/>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="catalog-loading">
            <div
              className="w-10 h-10 rounded-full border-4 border-neutral-200 animate-spin"
              style={{ borderTopColor: primary }}
            />
            <div className="text-sm" style={{ color: textMuted }}>Carregando catálogo…</div>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-neutral-500 py-20 text-sm" data-testid="catalog-empty">
            {search.trim() || activeCategory !== "all" ? "Nenhum produto encontrado." : "Este catálogo ainda não tem produtos."}
          </div>
        )}
      </section>

      {/* Product detail */}
      {openProduct && (
        <ProductDetail
          product={openProduct}
          primary={primary} accent={accent}
          onClose={closeProductModal}
          onAdd={addToCart}
          isDark={isDark}
          subtitlesMap={subtitlesMap}
        />
      )}

      {/* Store profile */}
      <StoreProfileDialog open={openProfile} onClose={()=>setOpenProfile(false)} store={data.store || {}} cfg={cfg} primary={primary} isDark={isDark}/>

      {/* Cart drawer */}
      <Dialog open={openCart} onOpenChange={setOpenCart}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Seu carrinho</DialogTitle></DialogHeader>
          {items.length === 0 ? (
            <div className="text-center py-12 text-sm text-neutral-500">Vazio.</div>
          ) : (
            <div className="space-y-3" data-testid="cart-list">
              {items.map(it => (
                <div key={it.key} className="flex gap-2 items-center border-b border-[#E5E5E5] dark:border-white/10 pb-3 min-w-0">
                  {it.photo
                    ? <img src={it.photo} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0"/>
                    : <div className="w-10 h-10 rounded bg-neutral-100 dark:bg-white/5 flex-shrink-0"/>}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" data-testid={`cart-item-name-${it.key}`}>{it.name}</div>
                    <div className="text-xs text-neutral-500">{brl(it.price)}</div>
                  </div>
                  <div className="flex items-center gap-0.5 border border-[#E5E5E5] dark:border-white/10 rounded-md flex-shrink-0">
                    <button onClick={()=>updQty(it.key, -1)} className="h-7 w-7 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5" aria-label="Diminuir"><Minus size={12}/></button>
                    <span className="text-xs w-5 text-center font-medium">{it.qty}</span>
                    <button onClick={()=>updQty(it.key, 1)} className="h-7 w-7 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5" aria-label="Aumentar"><Plus size={12}/></button>
                  </div>
                  <button onClick={()=>removeItem(it.key)} className="h-7 w-7 flex items-center justify-center text-neutral-400 hover:text-red-600 flex-shrink-0" aria-label="Remover"><X size={14}/></button>
                </div>
              ))}
              <div className="flex justify-between items-center font-display text-lg pt-2 gap-2">
                <span>Total</span>
                <span className="text-right" data-testid="cart-total">{brl(total)}</span>
              </div>
              <Button onClick={()=>{ setOpenCart(false); setOpenCheckout(true); }} className="w-full text-white whitespace-normal h-auto py-2.5" style={{ background: primary }} data-testid="go-to-checkout">
                Finalizar pedido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout */}
      <Dialog open={openCheckout} onOpenChange={setOpenCheckout}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Finalizar pedido</DialogTitle></DialogHeader>
          <div className="space-y-3 min-w-0">
            <div className="border-b border-[#E5E5E5] pb-3 space-y-1 text-sm max-h-40 overflow-y-auto">
              {items.map(it => (
                <div key={it.key} className="flex justify-between gap-2 min-w-0">
                  <span className="truncate flex-1 min-w-0">{it.name} × {it.qty}</span>
                  <span className="flex-shrink-0 whitespace-nowrap">{brl(it.price * it.qty)}</span>
                </div>
              ))}
              <div className="flex justify-between font-display text-base pt-2 gap-2"><span>Total</span><span className="whitespace-nowrap">{brl(total)}</span></div>
            </div>
            <div><Label>Nome *</Label><Input value={form.customer_name} onChange={e=>setForm({...form, customer_name: e.target.value})} data-testid="checkout-name"/></div>
            <div><Label>Telefone *</Label><Input value={form.customer_phone} onChange={e=>setForm({...form, customer_phone: e.target.value})} placeholder="(00) 00000-0000" data-testid="checkout-phone"/></div>
            <div><Label>Endereço</Label><Input value={form.customer_address} onChange={e=>setForm({...form, customer_address: e.target.value})}/></div>
            <div><Label>Observação</Label><Textarea value={form.note} onChange={e=>setForm({...form, note: e.target.value})} rows={2}/></div>
          </div>
          <DialogFooter className="flex-col sm:flex-col gap-2 sm:space-x-0">
            {whatsappUrl(data.store?.phone) && (
              <Button onClick={submitWhatsApp} className="w-full bg-green-500 hover:bg-green-600 text-white whitespace-normal h-auto py-2 flex flex-col gap-0.5" data-testid="checkout-whatsapp">
                <span className="flex items-center gap-2"><MessageCircle size={16} className="flex-shrink-0"/> Enviar pedido no WhatsApp</span>
                <span className="text-[11px] font-normal opacity-90 leading-tight">Melhor opção. A loja é notificada na hora</span>
              </Button>
            )}
            <Button onClick={submit} style={{ background: primary }} className="w-full text-white whitespace-normal h-auto py-2 flex flex-col gap-0.5" data-testid="checkout-submit">
              <span>Enviar pedido</span>
              <span className="text-[11px] font-normal opacity-80 leading-tight">Esta opção não notifica a loja no WhatsApp</span>
            </Button>
            <Button variant="outline" onClick={()=>setOpenCheckout(false)} className="w-full">Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {done && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-md p-8 max-w-sm text-center" data-testid="order-success">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 mx-auto flex items-center justify-center mb-3 text-xl">✓</div>
            <h3 className="font-display text-2xl">Pedido enviado!</h3>
            <p className="text-sm text-neutral-500 mt-2">Número: #{String(done.order_number).padStart(6,'0')}</p>
            <p className="text-sm text-neutral-500">Total: {brl(done.total)}</p>
            <p className="text-xs text-neutral-400 mt-4">Em breve a loja entrará em contato.</p>
            <Button className="mt-6 w-full" onClick={()=>{ setDone(null); resetCatalogToHome(); }} style={{ background: primary }}>Continuar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
